import { RadioGroup, Radio } from '@headlessui/react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IProtectionPlan, ProtectionPlanEnum } from '../../../../__generated__/graphql';

const StyledRadioGroup = styled(RadioGroup)`
  ${tw`space-y-2`}
`;

const StyledRadio = styled(Radio)<{ $declinedSelected?: boolean }>`
  ${tw`relative flex w-full max-w-full min-w-0 box-border cursor-pointer rounded-lg bg-bg-secondary py-2 px-5 text-white shadow-card transition`}
  &:focus {
    ${tw`outline-1 outline-red-500`}
  }
  &[data-checked] {
    ${tw`bg-green-500/10`}
  }
  ${({ $declinedSelected }) => $declinedSelected && tw`opacity-60 ring-1 ring-gray-200`}
`;

const RadioContent = styled.div`
  ${tw`flex flex-col w-full min-w-0 max-w-full justify-between`}
`;

const PlanInfo = styled.div`
  ${tw`text-sm/6 text-text-primary min-w-0 max-w-full break-words`}
`;

const PlanName = styled.p`
  ${tw`font-semibold text-text-primary`}
`;

const DeclineWrap = styled.div<{ $selected?: boolean }>`
  ${tw`rounded-lg transition`}
  ${({ $selected }) => $selected && tw`bg-green-500/10 border-2 border-green-600 px-5 py-3`}
`;

const DeclineButton = styled.button<{ hidden?: boolean; $selected?: boolean }>`
  ${tw`text-sm bg-transparent border-none cursor-pointer hover:text-text-primary`}
  ${({ hidden }) => hidden && tw`hidden`}
  ${({ $selected }) =>
    $selected
      ? tw`text-text-primary font-bold underline decoration-green-700`
      : tw`text-text-secondary font-bold underline`}
  &:disabled {
    ${tw`text-text-tertiary cursor-not-allowed hover:text-text-tertiary`}
  }
`;

const DeclineDisabledMessage = styled.div`
  ${tw`text-xs text-text-secondary -mt-1 mb-2`}
`;

export const PlanRadioList = ({
  plans,
  selected,
  setSelected,
  setShowDeclineDialog,
  showDeclineButton = true,
  rentalType,
  hasActiveMonthlyPlans,
  canDeclineProtection = true,
}: {
  plans: IProtectionPlan[];
  selected: IProtectionPlan | null;
  setSelected: (plan: IProtectionPlan) => void;
  setShowDeclineDialog: (show: boolean) => void;
  showDeclineButton?: boolean;
  rentalType?: string | null;
  hasActiveMonthlyPlans?: boolean;
  canDeclineProtection?: boolean;
}) => {
  const isMonthlyBooking = rentalType === 'MONTHLY';
  const shouldShowMonthlyPlans = isMonthlyBooking && hasActiveMonthlyPlans === true;
  const isDeclinedSelected = selected?.plan === ProtectionPlanEnum.Declined;
  return (
    <StyledRadioGroup value={selected} aria-label="Server size">
      {plans?.map(plan => {
        // Host-deactivated paid tiers must not appear in checkout / upgrade UIs
        if (plan.plan !== ProtectionPlanEnum.Declined && plan.isActive === false) {
          return null;
        }
        // Monthly booking: if host has active monthly plans, show only MONTHLY_* + DECLINED
        if (shouldShowMonthlyPlans && plan.plan !== ProtectionPlanEnum.Declined) {
          if (!plan.plan.startsWith('MONTHLY_')) return null;
        }
        // Monthly booking with no active monthly plans, or daily booking: hide MONTHLY_* paid tiers
        if (!shouldShowMonthlyPlans && plan.plan !== ProtectionPlanEnum.Declined) {
          if (plan.plan.startsWith('MONTHLY_')) return null;
        }
        if (plan.plan === ProtectionPlanEnum.Declined)
          return (
            <DeclineWrap key={plan.plan} $selected={isDeclinedSelected}>
              <DeclineButton
                hidden={!showDeclineButton}
                disabled={!canDeclineProtection}
                $selected={isDeclinedSelected}
                onClick={() => {
                  if (!canDeclineProtection) return;
                  setShowDeclineDialog(true);
                }}
              >
                {isDeclinedSelected
                  ? 'Decline coverage'
                  : "No thanks, I'd like to decline damage protection"}
              </DeclineButton>
              {showDeclineButton && !canDeclineProtection && (
                <DeclineDisabledMessage>
                  You cannot decline protection for this trip.
                </DeclineDisabledMessage>
              )}
            </DeclineWrap>
          );
        return (
          <StyledRadio
            key={plan.plan}
            value={plan}
            $declinedSelected={isDeclinedSelected}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setSelected(plan as IProtectionPlan);
            }}
          >
            <RadioContent>
              <PlanInfo>
                <PlanName>{plan.plan}</PlanName>
              </PlanInfo>
              <PlanInfo>
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{plan.description}</ReactMarkdown>
              </PlanInfo>
            </RadioContent>
          </StyledRadio>
        );
      })}
    </StyledRadioGroup>
  );
};
